import { Link, useLocation } from 'react-router';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';

import { paths } from 'src/routes/paths';

import { getUserDisplayName } from 'src/utils/get-user-display-name';

import { fonts } from 'src/theme';
import ltaLogo from 'src/assets/images/lta-logo.png';
import { highlight, neutralDark, neutralLight } from 'src/theme/palette';

import { Button } from 'src/components/button';

const NAV_LINKS = [
  { label: 'Dashboard', path: paths.dashboard },
  { label: 'Skills profile', path: paths.skillsProfile },
  { label: 'CV', path: paths.cv },
  { label: 'Explore', path: paths.explore },
];

const BORDER_COLOR = neutralLight.dark;

type NavBarUser = {
  firstName?: string | null;
  lastName?: string | null;
};

export interface NavBarProps {
  isAuthenticated?: boolean;
  user?: NavBarUser | null;
  onSignOut?: () => void;
  avatarColour?: string;
  onAvatarClick?: () => void;
}

export function NavBar({
  isAuthenticated = false,
  user,
  onSignOut,
  avatarColour,
  onAvatarClick,
}: NavBarProps) {
  const location = useLocation();

  const displayName = getUserDisplayName(user);

  const initial = displayName.charAt(0).toUpperCase() || '?';

  function handleSignOut() {
    onSignOut?.();
  }

  const headerSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: { xs: 3, md: '64px' },
    py: { xs: 4, md: '16px' },
    backgroundColor: neutralLight.lightest,
    borderBottom: { md: `1px solid ${BORDER_COLOR}`, sm: 'none' },
    width: '100%',
  };

  return (
    <Box component="header" sx={headerSx}>
      {/* Logo */}
      <Box
        component={Link}
        to={paths.root}
        aria-label="LTA Activators home"
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 32,
          flexShrink: 0,
          textDecoration: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: highlight.darkest,
            outlineOffset: 4,
          },
        }}
      >
        <Box component="img" src={ltaLogo} alt="" width={53} height={32} />
      </Box>

      {isAuthenticated ? (
        <>
          {/* Nav links */}
          <Box
            component="nav"
            aria-label="Main navigation"
            sx={{ display: 'flex', alignItems: 'center', gap: '32px', overflow: 'hidden' }}
          >
            {NAV_LINKS.map(({ label, path }) => {
              const active = location.pathname === path;
              return (
                <Typography
                  key={path}
                  component={Link}
                  to={path}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: 1,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    color: active ? highlight.darkest : neutralDark.dark,
                    '&:hover': { color: highlight.darkest },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: highlight.darkest,
                      outlineOffset: 4,
                    },
                    transition: 'color 0.15s',
                  }}
                >
                  {label}
                </Typography>
              );
            })}
          </Box>

          {/* User area */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
            <Box
              component={onAvatarClick ? 'button' : 'div'}
              onClick={onAvatarClick}
              aria-label={onAvatarClick ? 'Change profile colour' : undefined}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: avatarColour ?? highlight.darkest,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: 'none',
                p: 0,
                cursor: onAvatarClick ? 'pointer' : 'default',
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: highlight.darkest,
                  outlineOffset: 4,
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: fonts.body,
                  fontSize: 18,
                  fontWeight: 700,
                  color: neutralLight.lightest,
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {initial}
              </Typography>
            </Box>

            {displayName && (
              <Typography
                sx={{
                  fontFamily: fonts.body,
                  fontSize: 16,
                  fontWeight: 500,
                  color: neutralDark.dark,
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </Typography>
            )}

            <ButtonBase
              onClick={handleSignOut}
              sx={{
                fontFamily: fonts.body,
                fontSize: 16,
                fontWeight: 500,
                color: highlight.darkest,
                whiteSpace: 'nowrap',
                '&:hover': { opacity: 0.8 },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: highlight.darkest,
                  outlineOffset: 4,
                },
              }}
            >
              Sign out
            </ButtonBase>
          </Box>
        </>
      ) : (
        /* Unauthenticated right side */
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', flexShrink: 0 }}>
          <Typography
            component={Link}
            to={paths.signIn}
            sx={{
              fontFamily: fonts.body,
              fontSize: 16,
              fontWeight: 500,
              color: highlight.darkest,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { opacity: 0.8 },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: highlight.darkest,
                outlineOffset: 4,
              },
            }}
          >
            Sign in
          </Typography>

          <Button
            size="medium"
            href={paths.dashboard}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            Start your journey
          </Button>
        </Box>
      )}
    </Box>
  );
}
